// Nominees (§13.6, §17.12): up to 4 nominees whose shares total 100%.
// The whole list is saved in one go.
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { getNominees, saveNominees } from '@/api/profile';
import { ApiError } from '@/api/client';
import type { Nominee } from '@/types/phase2.types';
import Loader from '@/components/ui/Loader/Loader';
import Button from '@/components/ui/Button/Button';
import './profile.css';

const MAX_NOMINEES = 4;

interface Draft {
  id: string;
  name: string;
  relationship: string;
  share: string;
}

const toDraft = (n: Nominee): Draft => ({
  id: n.id,
  name: n.name,
  relationship: n.relationship,
  share: String(n.share),
});

const Nominees = () => {
  const navigate = useNavigate();
  const [drafts, setDrafts] = useState<Draft[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    getNominees()
      .then((list) => setDrafts(list.map(toDraft)))
      .catch(() => setDrafts([]));
  }, []);

  const total = (drafts ?? []).reduce((sum, d) => sum + (Number(d.share) || 0), 0);

  const update = (index: number, patch: Partial<Draft>) => {
    setDrafts((list) => (list ?? []).map((d, i) => (i === index ? { ...d, ...patch } : d)));
  };

  const addRow = () => {
    setDrafts((list) => [...(list ?? []), { id: '', name: '', relationship: '', share: '' }]);
  };

  const removeRow = (index: number) => {
    setDrafts((list) => (list ?? []).filter((_, i) => i !== index));
  };

  const save = async () => {
    if (!drafts) return;
    setError('');
    for (const d of drafts) {
      if (!d.name.trim() || !d.relationship.trim() || !(Number(d.share) > 0)) {
        setError('Fill in the name, relationship and a share above zero for every nominee.');
        return;
      }
    }
    setSaving(true);
    try {
      const saved = await saveNominees(
        drafts.map((d) => ({
          id: d.id,
          name: d.name.trim(),
          relationship: d.relationship.trim(),
          share: Number(d.share),
        }))
      );
      setDrafts(saved.map(toDraft));
      toast.success('Nominees saved');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save the nominees. Try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="nova-profile-page">
      <div className="nova-pay-flow-header">
        <button type="button" className="nova-icon-btn" title="Back" aria-label="Back" onClick={() => navigate('/profile')}>
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
        <h2>Nominees</h2>
      </div>

      {!drafts && <Loader rows={3} />}
      {drafts && (
        <>
          <p className="nova-pay-context-line">
            Up to {MAX_NOMINEES} nominees. Shares must total 100%.
          </p>

          {drafts.map((d, index) => (
            <div key={index} className="nova-nominee-row">
              <div className="row g-2 flex-grow-1">
                <div className="col-12 col-sm-5">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Nominee name"
                    aria-label="Nominee name"
                    value={d.name}
                    onChange={(e) => update(index, { name: e.target.value })}
                  />
                </div>
                <div className="col-8 col-sm-4">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Relationship"
                    aria-label="Relationship"
                    value={d.relationship}
                    onChange={(e) => update(index, { relationship: e.target.value })}
                  />
                </div>
                <div className="col-4 col-sm-3">
                  <div className="input-group">
                    <input
                      type="number"
                      min={1}
                      max={100}
                      className="form-control"
                      placeholder="Share"
                      aria-label="Share percent"
                      value={d.share}
                      onChange={(e) => update(index, { share: e.target.value })}
                    />
                    <span className="input-group-text">%</span>
                  </div>
                </div>
              </div>
              <button
                type="button"
                className="nova-icon-btn nova-icon-btn-danger"
                title="Remove nominee"
                aria-label="Remove nominee"
                onClick={() => removeRow(index)}
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
            </div>
          ))}

          <p className={`nova-nominee-total ${drafts.length === 0 || total === 100 ? 'ok' : 'bad'}`}>
            Total share: {total}%
          </p>
          {error && <div className="nova-limit-box">{error}</div>}

          <div className="nova-form-footer">
            <Button
              variant="outline"
              onClick={addRow}
              disabled={drafts.length >= MAX_NOMINEES}
            >
              <FontAwesomeIcon icon={faPlus} /> Add nominee
            </Button>
            <Button onClick={() => void save()} isLoading={saving} disabled={drafts.length > 0 && total !== 100}>
              Save nominees
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default Nominees;
